import React, { useState, useRef, useEffect, useContext } from "react";
import { DBContext } from './DBContext'
import SherDineNav from "./SherDineNav";
import SideNav from "./SideNav";

const FoodMenuScanner = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [extractedItems, setExtractedItems] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [isImporting, setIsImporting] = useState(false);
    const [importStatus, setImportStatus] = useState({});
    const [showImportModal, setShowImportModal] = useState(false);
    const [itemCodeCounter, setItemCodeCounter] = useState(1);

    // Database context and other states
    // const { database, company, user } = useContext(DBContext) || {};
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const fileInputRef = useRef(null);
    const uploadAreaRef = useRef(null);
    const [sideNav, setSideNav] = useState(false);
    const [falseState, setFalseState] = useState(null);

    const { customerCode, logginedDB, sideNavState, user_Id, sideNavStateFunc, rightNavState, rightNavStateFunc } = useContext(DBContext);

    const company = logginedDB
    const user = user_Id

    const handleSideNav = (state) => {
        setSideNav(state);
    };

    const handleFileSelect = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            processFile(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            processFile(files[0]);
        }
    };

    const processFile = (file) => {
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/bmp'];
        if (!validTypes.includes(file.type)) {
            setError('Please select a valid image file (JPG, PNG, WebP, BMP)');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            setError('File size must be less than 10MB');
            return;
        }

        setSelectedFile(file);
        setError('');
        setSuccess('');
        setShowResults(false);
        setSelectedItems([]);

        const reader = new FileReader();
        reader.onload = (e) => {
            setPreviewUrl(e.target.result);
        };
        reader.readAsDataURL(file);
    };

    const uploadAndProcess = async () => {
        if (!selectedFile) {
            setError('Please select an image file first');
            return;
        }

        setIsLoading(true);
        setError('');
        setSuccess('');

        try {
            const formData = new FormData();
            formData.append('menuImage', selectedFile);

            const response = await fetch(`${apiBaseUrl}/scanningImg/api/process-menu`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Server error: ${response.status}`);
            }

            const result = await response.json();
            setSuccess(`Successfully extracted ${result.itemsCount} items!`);
            setExtractedItems(result.items || []);
            setSelectedItems(new Array(result.items?.length || 0).fill(false));
            setShowResults(true);

        } catch (error) {
            console.error('Upload error:', error);
            setError(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleSelectItem = (index) => {
        const newSelected = [...selectedItems];
        newSelected[index] = !newSelected[index];
        setSelectedItems(newSelected);
    };

    const selectAllItems = () => {
        setSelectedItems(new Array(extractedItems.length).fill(true));
    };

    const deselectAllItems = () => {
        setSelectedItems(new Array(extractedItems.length).fill(false));
    };

    const getSelectedCount = () => {
        return selectedItems.filter(Boolean).length;
    };

    const prepareItemData = (item, index) => {
        // Generate item code (you might want a better logic)
        const itemCode = `ITEM${String(itemCodeCounter + index).padStart(3, '0')}`;

        // Prepare item details for AddItemReg API
        const itemDetails = {
            itemName: item.ITEM,
            itemCode: itemCode,
            FoodType: { label: item.VEG_NON_VEG === 'Veg' ? 'Vegetarian' : 'Non-Vegetarian', value: item.VEG_NON_VEG === 'Veg' ? 1 : 2 },
            Category: { label: item.CATEGORY, value: 1 }, // Default value, will be updated
            Kitchen: { label: 'Main Kitchen', value: 1 }, // Default kitchen
            regionalName: item.ITEM,
            Cess: '0',
            tax: { label: '5%', value: 1 } // Default tax
        };

        // Prepare price details
        const priceDetails = {
            normalPrice: item.RATE
        };

        // Prepare variant prices if any
        const variant_Price = item.VARIENTS && item.VARIENTS.length > 0
            ? item.VARIENTS.map(variant => ({
                itemName: variant.VARIENT,
                itemPrice: variant.RATE
            }))
            : [{ itemName: null, itemPrice: null }];

        return {
            itemDetails,
            priceDetails,
            variant_Price,
            itemCode
        };
    };

    const createCategoryIfNotExists = async (categoryName) => {
        if (!logginedDB || !company || !user) {
            throw new Error('Database context not available');
        }

        try {
            // First check if category exists
            const checkResponse = await fetch(`${apiBaseUrl}/api/check-category/${logginedDB}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ categoryName })
            });

            if (checkResponse.ok) {
                return true; // Category exists
            }

            // Create new category
            const categoryData = {
                CategoryName: categoryName,
                CategoryCode: categoryName.substring(0, 3).toUpperCase() + '001',
                Date: new Date().toISOString().split('T')[0]
            };

            const eventDetails = {
                user_Id: user.id,
                ddate: new Date().toISOString().split('T')[0],
                ttime: new Date().toTimeString().split(' ')[0]
            };

            const response = await fetch(`${apiBaseUrl}/CategoryReg/${logginedDB}/${encodeURIComponent(JSON.stringify(eventDetails))}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(categoryData)
            });

            if (!response.ok) {
                throw new Error(`Failed to create category: ${categoryName}`);
            }

            return true;
        } catch (error) {
            console.error('Error creating category:', error);
            throw error;
        }
    };

    const importSelectedItems = async () => {
        if (!logginedDB || !company || !user) {
            setError('Please make sure you are logged in and database is selected');
            return;
        }

        const selectedIndices = selectedItems.reduce((indices, isSelected, index) => {
            if (isSelected) indices.push(index);
            return indices;
        }, []);

        if (selectedIndices.length === 0) {
            setError('Please select at least one item to import');
            return;
        }

        setIsImporting(true);
        setImportStatus({});
        setShowImportModal(true);

        let successCount = 0;
        let failCount = 0;

        for (let i = 0; i < selectedIndices.length; i++) {
            const index = selectedIndices[i];
            const item = extractedItems[index];

            try {
                setImportStatus(prev => ({
                    ...prev,
                    [index]: { status: 'processing', message: 'Creating category...' }
                }));

                // Create category if needed
                await createCategoryIfNotExists(item.CATEGORY);

                setImportStatus(prev => ({
                    ...prev,
                    [index]: { status: 'processing', message: 'Preparing item data...' }
                }));

                // Prepare item data
                const { itemDetails, priceDetails, variant_Price, itemCode } = prepareItemData(item, index);

                setImportStatus(prev => ({
                    ...prev,
                    [index]: { status: 'processing', message: 'Uploading to database...' }
                }));

                // Prepare form data for AddItemReg API
                const formData = new FormData();
                formData.append('itemDetails', JSON.stringify(itemDetails));
                formData.append('priceDetails', JSON.stringify(priceDetails));
                formData.append('variant_Price', JSON.stringify(variant_Price));
                formData.append('Company', JSON.stringify(company));
                formData.append('img', ''); // No image for now

                const eventDetails = {
                    user_Id: user.id,
                    ddate: new Date().toISOString().split('T')[0],
                    ttime: new Date().toTimeString().split(' ')[0]
                };

                // Call AddItemReg API
                const response = await fetch(`${apiBaseUrl}/AddItemReg/${logginedDB}/${encodeURIComponent(JSON.stringify(eventDetails))}`, {
                    method: 'POST',
                    body: formData
                });

                if (response.ok) {
                    successCount++;
                    setImportStatus(prev => ({
                        ...prev,
                        [index]: { status: 'success', message: 'Item imported successfully!' }
                    }));
                } else {
                    const errorData = await response.json();
                    failCount++;
                    setImportStatus(prev => ({
                        ...prev,
                        [index]: { status: 'error', message: errorData.message || 'Import failed' }
                    }));
                }

            } catch (error) {
                console.error(`Error importing item ${index}:`, error);
                failCount++;
                setImportStatus(prev => ({
                    ...prev,
                    [index]: { status: 'error', message: error.message || 'Import failed' }
                }));
            }
        }

        // Update item code counter
        setItemCodeCounter(prev => prev + selectedIndices.length);

        setIsImporting(false);
        setSuccess(`Imported ${successCount} items successfully. ${failCount} items failed.`);
    };

    const getCategoryCount = () => {
        const categories = new Set(extractedItems.map(item => item.CATEGORY || 'Uncategorized'));
        return categories.size;
    };

    const bulkRegisterItems = async () => {
        if (extractedItems.length === 0) {
            setError('No items to register');
            return;
        }

        setIsLoading(true);
        setError('');
        setSuccess('');

        try {
            // Get event details from context or localStorage
            const eventDetails = {
                user_Id: 1, // Replace with actual user ID from context
                ddate: new Date().toISOString().split('T')[0],
                ttime: new Date().toLocaleTimeString()
            };

            const Company = JSON.parse(localStorage.getItem('Company') || '""');
            const dbName = logginedDB; // use the tenant DB from context

            const response = await fetch(`${apiBaseUrl}/bulk-register-items/${dbName}/${encodeURIComponent(JSON.stringify(eventDetails))}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    extractedItems,
                    Company: JSON.stringify(Company)
                })
            });

            const result = await response.json();

            if (response.ok && result.success) {
                setSuccess(result.message);
                // Update UI to show registered items
                const registeredItems = result.details.registeredItemNames;
                const skippedItems = result.details.skippedItemNames;

                // Update extracted items to show status
                setExtractedItems(prevItems =>
                    prevItems.map(item => ({
                        ...item,
                        isRegistered: registeredItems.includes(item.ITEM || item.ItemName),
                        isSkipped: skippedItems.includes(item.ITEM || item.ItemName)
                    }))
                );
            } else {
                setError(result.message || 'Registration failed');
            }
        } catch (error) {
            console.error('Bulk registration error:', error);
            setError(`Registration error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // const renderItemCard = (item, index) => {
    //     const itemName = item.ITEM || 'Unnamed Item';
    //     const category = item.CATEGORY || 'Uncategorized';
    //     const rate = item.RATE;
    //     const variants = item.VARIENTS || [];
    //     const vegStatus = item.VEG_NON_VEG || 'Unknown';

    //     return (
    //         <div key={index} className={`
    //             bg-white border rounded-[10px] p-5 transition-all duration-300 
    //             ${selectedItems[index] ? 'border-[#667eea] border-2 shadow-lg' : 'border-[#e1e5e9] hover:border-[#667eea] hover:-translate-y-[2px] hover:shadow-lg'}
    //         `}>
    //             <div className="flex justify-between items-start mb-3">
    //                 <div className="flex-1">
    //                     <div className="text-[1.2rem] font-semibold text-[#333] mb-2 flex items-center gap-2">
    //                         {itemName}
    //                         <span className={`
    //                             text-xs px-2 py-1 rounded-full
    //                             ${vegStatus === 'Veg' ? 'bg-green-100 text-green-800' :
    //                                 vegStatus === 'Non-Veg' ? 'bg-red-100 text-red-800' :
    //                                     vegStatus === 'Egg' ? 'bg-yellow-100 text-yellow-800' :
    //                                         'bg-gray-100 text-gray-800'}
    //                         `}>
    //                             {vegStatus}
    //                         </span>
    //                     </div>
    //                     <div className="inline-block bg-[#eef2ff] text-[#667eea] px-3 py-1 rounded-full text-sm mb-3 font-medium">
    //                         {category}
    //                     </div>
    //                 </div>
    //                 <button
    //                     onClick={() => toggleSelectItem(index)}
    //                     className={`
    //                         w-6 h-6 rounded-full border-2 flex items-center justify-center
    //                         ${selectedItems[index]
    //                             ? 'bg-[#667eea] border-[#667eea] text-white'
    //                             : 'border-gray-300 hover:border-[#667eea]'
    //                         }
    //                     `}
    //                 >
    //                     {selectedItems[index] && '✓'}
    //                 </button>
    //             </div>

    //             <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    //                 <div className="bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white p-2 rounded-lg text-center">
    //                     <div className="text-sm opacity-90">Base Price</div>
    //                     <div className="text-xl font-bold mt-1">₹{rate ? parseFloat(rate).toFixed(2) : '0.00'}</div>
    //                 </div>
    //             </div>

    //             {variants.length > 0 && (
    //                 <div className="bg-[#f8f9ff] rounded-lg p-3 mt-3">
    //                     <h4 className="text-[#555] mb-2 text-sm uppercase tracking-wider">Available Variants</h4>
    //                     {variants.map((variant, idx) => (
    //                         <div key={idx} className="flex justify-between py-2 border-b border-[#e1e5e9] last:border-b-0">
    //                             <span className="text-[#666]">
    //                                 {variant.VARIENT || 'Variant'}
    //                             </span>
    //                             <span className="text-[#667eea] font-semibold">
    //                                 ₹{parseFloat(variant.RATE).toFixed(2)}
    //                             </span>
    //                         </div>
    //                     ))}
    //                 </div>
    //             )}
    //         </div>
    //     );
    // };

    const renderItemCard = (item, index) => {
        const itemName = item.ITEM || item.ItemName || 'Unnamed Item';
        const category = item.CATEGORY || item.CategoryName || 'Uncategorized';
        const rate = item.RATE || item.BaseRate;
        const variants = item.VARIENTS || item.Variants || [];
        const isVeg = item.VEG_NON_VEG === 'Veg';
        const isNonVeg = item.VEG_NON_VEG === 'Non-Veg';

        return (
            <div key={index} className={`
            bg-white border rounded-[10px] p-5 transition-all duration-300 
            ${item.isRegistered ? 'border-green-500 bg-green-50' :
                    item.isSkipped ? 'border-yellow-500 bg-yellow-50' :
                        'border-[#e1e5e9] hover:border-[#667eea]'}
            hover:shadow-lg hover:-translate-y-[2px]
        `}>
                <div className="flex justify-between items-start mb-2">
                    <div className="text-[1.2rem] font-semibold text-[#333]">{itemName}</div>
                    {item.isRegistered && (
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                            ✓ Registered
                        </span>
                    )}
                    {item.isSkipped && (
                        <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                            ⚠️ Skipped
                        </span>
                    )}
                </div>

                <div className="flex flex-wrap gap-2 mb-3">
                    <div className="inline-block bg-[#eef2ff] text-[#667eea] px-3 py-1 rounded-full text-sm font-medium">
                        {category}
                    </div>
                    {isVeg && (
                        <div className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                            🥗 Veg
                        </div>
                    )}
                    {isNonVeg && (
                        <div className="inline-block bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                            🥩 Non-Veg
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {rate && (
                        <div className="bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white p-2 rounded-lg text-center">
                            <div className="text-sm opacity-90">Base Price</div>
                            <div className="text-xl font-bold mt-1">₹{parseFloat(rate).toFixed(2)}</div>
                        </div>
                    )}
                </div>

                {variants.length > 0 && (
                    <div className="bg-[#f8f9ff] rounded-lg p-3 mt-3">
                        <h4 className="text-[#555] mb-2 text-sm uppercase tracking-wider">Available Variants</h4>
                        {variants.map((variant, idx) => (
                            <div key={idx} className="flex justify-between py-2 border-b border-[#e1e5e9] last:border-b-0">
                                <span className="text-[#666]">
                                    {variant.VARIENT || variant.VariantName || 'Variant'}
                                </span>
                                <span className="text-[#667eea] font-semibold">
                                    ₹{parseFloat(variant.RATE || variant.Rate).toFixed(2)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="w-screen h-screen flex flex-col bg-[#f5f5f5] overflow-hidden">
            {/* Fixed Top Navigation */}
            <div className="flex-shrink-0">
                <SherDineNav sideNav={(state) => handleSideNav(state)} falseState={falseState} />
            </div>

            {/* Main Content Area */}
            <div className="flex gap-3 flex-1 overflow-hidden">
                {/* Fixed Sidebar */}
                <div className="flex-shrink-0 h-full z-[9999]">
                    <SideNav focus={'Menu'} state={sideNav} falseState={(state) => setFalseState(state)} />
                </div>

                {/* Scrollable Main Content */}
                <div className="flex-1 flex flex-col overflow-hidden pr-3 pb-3">
                    {/* Scrollable Food Menu Scanner Content */}
                    <div className="flex-1 bg-white rounded-t-[5px] overflow-y-auto">
                        <div className="min-h-full flex justify-center items-start p-5 bg-gradient-to-br from-[#667eea] to-[#764ba2]">
                            <div className="bg-white rounded-[20px] shadow-2xl w-full max-w-6xl overflow-hidden">
                                {/* Header */}
                                <div className="bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white p-10 text-center">
                                    <h1 className="text-3xl md:text-4xl font-bold mb-3">🍽️ Food Menu Scanner</h1>
                                    <p className="text-lg opacity-90">
                                        Upload a menu image to extract food items, prices, and categories
                                    </p>
                                </div>

                                {/* Main Content */}
                                <div className="p-8 md:p-10">
                                    {/* Upload Area */}
                                    {!showResults && (
                                        <div
                                            ref={uploadAreaRef}
                                            onClick={handleFileSelect}
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={handleDrop}
                                            className={`
                                                border-3 border-dashed border-[#ddd] rounded-[15px] p-10 md:p-14 text-center mb-8
                                                transition-all duration-300 cursor-pointer
                                                hover:border-[#667eea] hover:bg-[#f8f9ff]
                                                ${isDragging ? 'border-[#667eea] bg-[#f0f2ff] scale-[1.02]' : ''}
                                            `}
                                        >
                                            <div className="text-5xl md:text-6xl mb-5 text-[#667eea]">📸</div>
                                            <h3 className="text-xl md:text-2xl font-semibold text-[#333] mb-3">
                                                Upload Menu Image
                                            </h3>
                                            <p className="text-[#666] mb-6">
                                                Drag & drop or click to select an image file (JPG, PNG, WebP, BMP)
                                            </p>
                                            <button
                                                className={`
                                                    bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white border-none
                                                    px-8 py-3 md:px-10 md:py-4 rounded-[50px] text-base md:text-lg
                                                    font-semibold cursor-pointer transition-all duration-300
                                                    hover:-translate-y-[2px] hover:shadow-xl
                                                    disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none
                                                    ${isLoading ? 'relative pr-14' : ''}
                                                `}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleFileSelect();
                                                }}
                                                disabled={isLoading}
                                            >
                                                <span>
                                                    {isLoading ? 'Processing...' : 'Choose Image'}
                                                </span>
                                                {isLoading && (
                                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                                )}
                                            </button>
                                            <input
                                                type="file"
                                                id="fileInput"
                                                ref={fileInputRef}
                                                accept=".jpg,.jpeg,.png,.webp,.bmp,image/*"
                                                onChange={handleFileChange}
                                                className="hidden"
                                            />
                                        </div>
                                    )}

                                    {/* Image Preview */}
                                    {previewUrl && !showResults && (
                                        <div className="mb-8 animate-fadeIn">
                                            <h3 className="text-lg font-semibold text-[#333] mb-4">Image Preview</h3>
                                            <img
                                                src={previewUrl}
                                                alt="Menu Preview"
                                                className="max-w-full max-h-72 rounded-[10px] shadow-lg mx-auto"
                                                onError={(e) => {
                                                    console.error('Image load error');
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                            {!isLoading && (
                                                <div className="text-center mt-6">
                                                    <button
                                                        className="bg-gradient-to-br from-[#2ecc71] to-[#27ae60] text-white border-none px-10 py-4 rounded-[50px] text-lg font-semibold cursor-pointer transition-all duration-300 hover:-translate-y-[2px] hover:shadow-xl"
                                                        onClick={uploadAndProcess}
                                                        disabled={isLoading}
                                                    >
                                                        {isLoading ? 'Processing...' : 'Extract Menu Items'}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Error Message */}
                                    {error && (
                                        <div className="mb-8 bg-[#fee] text-[#c33] p-4 rounded-[10px] border-l-4 border-[#c33] animate-shake">
                                            <strong>Error:</strong> {error}
                                            <div className="text-xs mt-1">
                                                Check if server is running on port {apiBaseUrl}
                                            </div>
                                        </div>
                                    )}

                                    {/* Success Message */}
                                    {success && (
                                        <div className="mb-8 bg-[#efffee] text-[#2a7] p-4 rounded-[10px] border-l-4 border-[#2a7] animate-fadeIn">
                                            {success}
                                        </div>
                                    )}

                                    {/* Results Section */}
                                    {showResults && extractedItems.length > 0 && (
                                        <div className="mt-8 animate-fadeIn">
                                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                                                <div>
                                                    <h2 className="text-2xl font-bold text-[#333] mb-2">📋 Extracted Menu Items</h2>
                                                    <p className="text-[#666]">
                                                        Found {extractedItems.length} items across {getCategoryCount()} categories
                                                    </p>
                                                </div>

                                                <div className="flex flex-col sm:flex-row gap-3">
                                                    <div className="flex items-center gap-3">
                                                        <button
                                                            onClick={selectAllItems}
                                                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                                        >
                                                            Select All
                                                        </button>
                                                        <button
                                                            onClick={deselectAllItems}
                                                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                                        >
                                                            Deselect All
                                                        </button>
                                                        <span className="text-sm text-gray-600">
                                                            {getSelectedCount()} selected
                                                        </span>
                                                    </div>
                                                    <button
                                                        onClick={importSelectedItems}
                                                        disabled={getSelectedCount() === 0 || isImporting}
                                                        className={`
                                                            bg-gradient-to-br from-[#2ecc71] to-[#27ae60] text-white
                                                            px-6 py-3 rounded-lg font-semibold transition-all duration-300
                                                            hover:-translate-y-[1px] hover:shadow-lg
                                                            disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                                                        `}
                                                    >
                                                        {isImporting ? 'Importing...' : `Import Selected (${getSelectedCount()})`}
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-h-[500px] overflow-y-auto p-3">
                                                {extractedItems.map((item, index) => renderItemCard(item, index))}
                                            </div>
                                        </div>
                                    )}

                                    {/* No Results */}
                                    {showResults && extractedItems.length === 0 && (
                                        <div className="text-center py-10">
                                            <div className="text-5xl mb-4">😕</div>
                                            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Items Found</h3>
                                            <p className="text-gray-500 mb-6">Could not extract any food items from the image.</p>
                                            <button
                                                onClick={() => {
                                                    setShowResults(false);
                                                    setSelectedFile(null);
                                                    setPreviewUrl('');
                                                }}
                                                className="bg-[#667eea] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#5a67d8] transition-colors"
                                            >
                                                Try Another Image
                                            </button>
                                        </div>
                                    )}

                                    {showResults && (
                                        <div className="mt-6 mb-4 p-4 bg-blue-50 rounded-lg">
                                            <h3 className="text-lg font-semibold text-blue-800 mb-2">Registration Summary</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div className="text-center">
                                                    <div className="text-2xl font-bold text-blue-600">{extractedItems.length}</div>
                                                    <div className="text-sm text-gray-600">Total Items</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="text-2xl font-bold text-green-600">
                                                        {extractedItems.filter(item => item.isRegistered).length}
                                                    </div>
                                                    <div className="text-sm text-gray-600">Registered</div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="text-2xl font-bold text-yellow-600">
                                                        {extractedItems.filter(item => item.isSkipped).length}
                                                    </div>
                                                    <div className="text-sm text-gray-600">Skipped</div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Import Progress Modal */}
            {showImportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
                        <div className="p-6 border-b">
                            <div className="flex justify-between items-center">
                                <h3 className="text-xl font-bold text-gray-800">Import Progress</h3>
                                <button
                                    onClick={() => setShowImportModal(false)}
                                    className="text-gray-500 hover:text-gray-700 text-2xl"
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[60vh]">
                            <div className="space-y-4">
                                {extractedItems.map((item, index) => (
                                    selectedItems[index] && (
                                        <div key={index} className="border rounded-lg p-4">
                                            <div className="flex justify-between items-center mb-2">
                                                <span className="font-medium text-gray-800">{item.ITEM}</span>
                                                {importStatus[index] && (
                                                    <span className={`
                                                        px-2 py-1 rounded-full text-xs font-medium
                                                        ${importStatus[index].status === 'processing' ? 'bg-blue-100 text-blue-800' :
                                                            importStatus[index].status === 'success' ? 'bg-green-100 text-green-800' :
                                                                'bg-red-100 text-red-800'}
                                                    `}>
                                                        {importStatus[index].status === 'processing' ? 'Processing...' :
                                                            importStatus[index].status === 'success' ? 'Success' : 'Failed'}
                                                    </span>
                                                )}
                                            </div>
                                            {importStatus[index] && (
                                                <div className="text-sm text-gray-600 mt-1">
                                                    {importStatus[index].message}
                                                </div>
                                            )}
                                            {!importStatus[index] && (
                                                <div className="text-sm text-gray-400">Waiting to start...</div>
                                            )}
                                        </div>
                                    )
                                ))}
                                {showResults && extractedItems.length > 0 && (
                                    <div className="text-center mt-6 mb-4">
                                        <button
                                            className="bg-gradient-to-br from-blue-500 to-purple-600 text-white px-8 py-3 rounded-full text-lg font-semibold hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                                            onClick={bulkRegisterItems}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? 'Registering Items...' : 'Register All Items to Database'}
                                        </button>
                                        <p className="text-sm text-gray-500 mt-2">
                                            This will add all extracted items to your menu database
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-6 border-t bg-gray-50">
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600">
                                    {isImporting ? 'Importing...' : 'Import completed'}
                                </span>
                                <button
                                    onClick={() => setShowImportModal(false)}
                                    className="bg-[#667eea] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#5a67d8] transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FoodMenuScanner;
